import { ConnectedOverlayPositionChange, ConnectedPosition } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import {
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  inject,
} from '@angular/core';
import { ButtonBaseComponent } from '../../buttons/atoms/button/button-base.component';
import { createCloseOnScrollOverlayStrategy } from '../../utils/overlay-scroll-strategy.util';
import {
  CoreUIContextMenuAppearance,
  CoreUIContextMenuItem,
  CoreUIContextMenuSize,
  CoreUIContextMenuVariant,
} from '../core-ui-context-menu/core-ui-context-menu.component';
import { CoreUIDropdownMenuItemDirective } from './core-ui-dropdown-menu-item.directive';

export type CoreUIDropdownMenuPlacement =
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'right'
  | 'bottom'
  | 'bottom-left'
  | 'bottom-right'
  | 'left';

export type CoreUIDropdownMenuTriggerSize = 'sm' | 'md' | 'lg';
export type CoreUIDropdownMenuTriggerIconSize = 'sm' | 'md' | 'base' | 'lg' | number | `${number}`;
export type CoreUIDropdownMenuTriggerIconDirection = 'none' | 'up' | 'down' | 'left' | 'right';

const TOP_LEFT_POSITIONS: ConnectedPosition[] = [
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
];

const TOP_RIGHT_POSITIONS: ConnectedPosition[] = [
  { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
  { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
];

const BOTTOM_LEFT_POSITIONS: ConnectedPosition[] = [
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
];

const BOTTOM_RIGHT_POSITIONS: ConnectedPosition[] = [
  { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
  { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
];

const POSITIONS: Record<CoreUIDropdownMenuPlacement, ConnectedPosition[]> = {
  top: TOP_RIGHT_POSITIONS,
  'top-left': TOP_LEFT_POSITIONS,
  'top-right': TOP_RIGHT_POSITIONS,
  right: [
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 6 },
    { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -6 },
  ],
  bottom: BOTTOM_RIGHT_POSITIONS,
  'bottom-left': BOTTOM_LEFT_POSITIONS,
  'bottom-right': BOTTOM_RIGHT_POSITIONS,
  left: [
    { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -6 },
    { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 6 },
  ],
};

@Component({
  selector: 'core-ui-dropdown-menu',
  templateUrl: './core-ui-dropdown-menu.component.html',
  styleUrls: ['./core-ui-dropdown-menu.component.scss'],
  host: {
    '[class.is-open]': 'isOpen',
  },
  standalone: false,
})
export class CoreUIDropdownMenuComponent implements OnDestroy {
  @ViewChild('trigger') private trigger?: ButtonBaseComponent;
  @ContentChildren(CoreUIDropdownMenuItemDirective, { descendants: true })
  private itemDefinitions?: QueryList<CoreUIDropdownMenuItemDirective>;

  @Input() ariaLabel = 'Acciones';
  @Input() triggerAriaLabel = 'Mostrar acciones';
  @Input() appearance: CoreUIContextMenuAppearance = 'light';
  @Input() size: CoreUIContextMenuSize = 'md';
  @Input() menuWidth?: string;
  @Input() variant: CoreUIContextMenuVariant = 'default';
  @Input() placement: CoreUIDropdownMenuPlacement = 'bottom';
  @Input() triggerSize: CoreUIDropdownMenuTriggerSize = 'md';
  @Input() triggerIcon = 'more_vert';
  @Input() triggerIconColor?: string;
  @Input() triggerIconSize: CoreUIDropdownMenuTriggerIconSize = 'base';
  @Input() triggerIconDirection: CoreUIDropdownMenuTriggerIconDirection = 'none';
  @Input() viewportMargin = 8;
  @Input() disabled = false;

  @Output() actionSelected = new EventEmitter<string>();
  @Output() openChange = new EventEmitter<boolean>();

  isOpen = false;
  menuPosition: 'above' | 'below' = 'below';
  readonly push = true;
  readonly scrollStrategy = createCloseOnScrollOverlayStrategy(inject(DOCUMENT));
  private closeStateTimeout?: ReturnType<typeof setTimeout>;

  get items(): readonly CoreUIContextMenuItem[] {
    return (this.itemDefinitions?.toArray() ?? []).map((item) => ({
      id: item.id,
      label: item.label,
      icon: item.icon,
      iconSize: item.iconSize,
      disabled: item.disabled,
      destructive: item.variant === 'danger',
      separatorBefore: item.separatorBefore,
    }));
  }

  get positions(): ConnectedPosition[] {
    return POSITIONS[this.placement];
  }

  get isTriggerDisabled(): boolean {
    return this.disabled || !this.items.some((item) => !item.disabled);
  }

  toggle(): void {
    if (this.isTriggerDisabled) return;

    if (this.isOpen) {
      this.close(true);
      return;
    }

    this.menuPosition = this.placement.startsWith('top') ? 'above' : 'below';
    this.setOpen(true);
  }

  selectAction(actionId: string): void {
    this.close();
    this.actionSelected.emit(actionId);
  }

  close(restoreFocus = false): void {
    this.setOpen(false);

    if (restoreFocus) {
      queueMicrotask(() => this.trigger?.focus());
    }
  }

  onPositionChange(change: ConnectedOverlayPositionChange): void {
    this.menuPosition = change.connectionPair.overlayY === 'bottom' ? 'above' : 'below';
  }

  onOverlayDetach(): void {
    this.isOpen = false;
    this.clearCloseStateTimeout();
    this.closeStateTimeout = setTimeout(() => {
      this.closeStateTimeout = undefined;
      if (!this.isOpen) this.openChange.emit(false);
    }, 0);
  }

  ngOnDestroy(): void {
    this.clearCloseStateTimeout();
  }

  private setOpen(isOpen: boolean): void {
    if (this.isOpen === isOpen) return;

    if (isOpen) this.clearCloseStateTimeout();
    this.isOpen = isOpen;
    if (isOpen) this.openChange.emit(true);
  }

  private clearCloseStateTimeout(): void {
    if (this.closeStateTimeout === undefined) return;

    clearTimeout(this.closeStateTimeout);
    this.closeStateTimeout = undefined;
  }
}
